import React from 'react';
import DeviceDetectionTest from '../components/DeviceDetectionTest';
import { getDeviceInfo, getAppBundleId, isInMobileApp, isIOSDevice, getAppFramework, shouldShowIOSWebViewAlert, showIOSWebViewAlert, getIOSWebViewDetectionDetails } from '../utils/deviceDetection';

const DeviceTest: React.FC = () => {
  const deviceInfo = getDeviceInfo();
  const bundleId = getAppBundleId();
  const inMobileApp = isInMobileApp();
  const isIOS = isIOSDevice();
  const appFramework = getAppFramework();
  const iosDetectionDetails = getIOSWebViewDetectionDetails();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">
          Device Detection & Bundle ID Test
        </h1>
        
        {/* Detailed Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Device Information</h2>
            <div className="space-y-2 text-sm">
              <div><strong>Platform:</strong> {deviceInfo.platform}</div>
              <div><strong>Is iOS:</strong> {isIOS ? 'Yes' : 'No'}</div>
              <div><strong>Is Android:</strong> {deviceInfo.isAndroid ? 'Yes' : 'No'}</div>
              <div><strong>Is Mobile:</strong> {deviceInfo.isMobile ? 'Yes' : 'No'}</div>
              <div><strong>Is Tablet:</strong> {deviceInfo.isTablet ? 'Yes' : 'No'}</div>
              <div><strong>Is Desktop:</strong> {deviceInfo.isDesktop ? 'Yes' : 'No'}</div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">App Information</h2>
            <div className="space-y-2 text-sm">
              <div><strong>In Mobile App:</strong> {inMobileApp ? 'Yes' : 'No'}</div>
              <div><strong>Bundle ID:</strong> {bundleId || 'Not detected'}</div>
              <div><strong>App Framework:</strong> {appFramework.framework}</div>
              {appFramework.version && <div><strong>Framework Version:</strong> {appFramework.version}</div>}
              {appFramework.platform && <div><strong>Framework Platform:</strong> {appFramework.platform}</div>}
              <div><strong>User Agent:</strong> 
                <div className="mt-1 p-2 bg-gray-100 rounded text-xs break-all">
                  {deviceInfo.userAgent}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Component Test */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">DeviceDetectionTest Component</h2>
          <DeviceDetectionTest />
        </div>

        {/* Detailed iOS WebView Detection */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">Detailed iOS WebView Detection</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div><strong>Is iOS:</strong> {iosDetectionDetails.isIOS ? 'Yes' : 'No'}</div>
              <div><strong>Platform:</strong> {iosDetectionDetails.platform}</div>
              <div><strong>Max Touch Points:</strong> {iosDetectionDetails.maxTouchPoints}</div>
              <div><strong>Standalone:</strong> {iosDetectionDetails.standalone ? 'Yes' : 'No'}</div>
              <div><strong>Display Mode:</strong> {iosDetectionDetails.displayMode}</div>
              <div><strong>Should Show Alert:</strong> {iosDetectionDetails.shouldShowAlert ? 'Yes' : 'No'}</div>
            </div>
            <div className="space-y-2">
              <div><strong>React Native WebView:</strong> {iosDetectionDetails.reactNativeWebView ? 'Yes' : 'No'}</div>
              <div><strong>Cordova:</strong> {iosDetectionDetails.cordova ? 'Yes' : 'No'}</div>
              <div><strong>Capacitor:</strong> {iosDetectionDetails.capacitor ? 'Yes' : 'No'}</div>
              <div><strong>Expo WebView:</strong> {iosDetectionDetails.expoWebView ? 'Yes' : 'No'}</div>
              <div><strong>Bundle ID Pattern:</strong> {iosDetectionDetails.bundleIdPattern ? 'Yes' : 'No'}</div>
              <div><strong>Explicit WebView:</strong> {iosDetectionDetails.explicitWebView ? 'Yes' : 'No'}</div>
              <div><strong>iOS WebView:</strong> {iosDetectionDetails.isIOSWebView ? 'Yes' : 'No'}</div>
              <div><strong>iOS PWA:</strong> {iosDetectionDetails.isIOSPWA ? 'Yes' : 'No'}</div>
            </div>
          </div>
          <div className="mt-4">
            <div><strong>User Agent:</strong></div>
            <div className="mt-1 p-2 bg-blue-100 rounded text-xs break-all">
              {iosDetectionDetails.userAgent}
            </div>
          </div>
        </div>

        {/* iOS WebView Alert Test */}
        <div className="mt-8 bg-orange-50 border border-orange-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-orange-900 mb-2">iOS WebView Alert Test</h3>
          <div className="text-orange-800 space-y-3">
            <p><strong>Current Status:</strong> {shouldShowIOSWebViewAlert() ? 'Should show alert' : 'No alert needed'}</p>
            <button
              onClick={() => showIOSWebViewAlert()}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
            >
              Test iOS WebView Alert
            </button>
            <p className="text-sm">This will show an alert popup if iOS + WebView is detected</p>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">How to Test Bundle ID Detection</h3>
          <div className="text-blue-800 space-y-2">
            <p><strong>1. In a regular browser:</strong> Bundle ID will show "Not detected", Framework: "unknown"</p>
            <p><strong>2. In a React Native app:</strong> Will detect framework and any bundle ID in user agent</p>
            <p><strong>3. In a Cordova app:</strong> Will detect Cordova framework and bundle ID</p>
            <p><strong>4. In a Capacitor app:</strong> Will detect Capacitor framework and bundle ID</p>
            <p><strong>5. In an Expo app:</strong> Will detect Expo framework and version</p>
            <p><strong>6. In a PWA:</strong> Will detect PWA framework</p>
            <p><strong>7. In a WebView:</strong> Will detect WebView framework and may find bundle ID</p>
            <p><strong>8. In iOS WebView:</strong> Will automatically show alert popup on page load</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceTest;
